/**
 * Manifold axis fields, sweep ranges, and Z-bound clamping.
 *
 * Axes split into:
 *   - SweepField — sweepable inputs (X / Y eligible)
 *   - OutputField — engine outputs (Z eligible)
 *
 * Sweep ranges anchor on the user's current scenario (evalSpot, IV, DTE) so the
 * surface always frames the trade you're actually looking at. Z bounds mirror
 * PayoffDiagram's "always include zero, symmetric ±absMax × 1.15" rule for
 * pnl/theta; one-sided greeks get plain min/max + 5% padding.
 */

export type SweepField = "price" | "strike" | "moneyness" | "iv" | "dte";
/** Engine-derived scalars eligible for the Z dropdown AND the colormap. P&L
 *  was originally excluded from Z when the colormap was hardcoded to P&L;
 *  once the colormap picker landed, the same set serves both slots and the
 *  AxisPill `lockedValue` mechanism keeps the user from picking the same
 *  field for Z and λ at once. */
export type OutputField =
  | "pnl"
  | "premium"
  | "theta"
  | "delta"
  | "gamma"
  | "vega";
/** Colormap signal — alias kept for clarity at colormap callsites. */
export type ColorField = OutputField;

export type ManifoldAxes = {
  x: SweepField;
  y: SweepField;
  z: OutputField;
  colorBy: ColorField;
};

export const SWEEP_FIELDS: readonly SweepField[] = [
  "price",
  "strike",
  "moneyness",
  "iv",
  "dte",
] as const;
// P&L leads the list because it's the most directly tradable surface, so
// users scanning the Z / λ pickers see it first.
export const OUTPUT_FIELDS: readonly OutputField[] = [
  "pnl",
  "premium",
  "theta",
  "delta",
  "gamma",
  "vega",
] as const;

// Display labels for sweep / output axes live in the locale-aware
// `useAxisLabels()` hook (`src/hooks/useLabels.ts`) — keep these names as the
// canonical keys so consumers index the hook's returned record by SweepField /
// OutputField. The greek glyphs below are not translated.

/** Greek letter glyphs for outputs that have one. Premium has no glyph.
 *  Exported so consumers can render the glyph in the STIX serif (font-greek)
 *  while the surrounding name stays in the consumer's chosen font. */
export const OUTPUT_GREEKS: Partial<Record<OutputField, string>> = {
  theta: "Θ",
  delta: "Δ",
  gamma: "Γ",
  vega: "ν",
};

/** True for sweep fields that re-anchor the structure (per-cell layer rebuild). */
export function shiftsStructure(field: SweepField): boolean {
  return field === "strike" || field === "moneyness";
}

/**
 * Output fields that straddle zero — render with symmetric Z bounds and a
 * centered zero plane. Other fields use plain min/max bounds.
 */
const ZERO_STRADDLING: ReadonlySet<OutputField> = new Set([
  "pnl",
  "theta",
  "delta",
]);

export function isZeroStraddling(field: OutputField): boolean {
  return ZERO_STRADDLING.has(field);
}

/** Default half-width of price/strike sweeps as a percent of the live market price. */
export const DEFAULT_PRICE_RANGE_PCT = 0.03;

/** Default sweep ranges anchored on the current scenario. */
export function priceRange(
  evalSpot: number,
  rangePct = DEFAULT_PRICE_RANGE_PCT,
): [number, number] {
  const halfRange = Math.max(evalSpot * rangePct, 1);
  return [evalSpot - halfRange, evalSpot + halfRange];
}

/** IV sweep: [0.5×, 2.0×] of current IV, clamped to [1%, 200%]. */
export function ivRange(currentIv: number): [number, number] {
  const lo = Math.max(0.01, currentIv * 0.5);
  const hi = Math.min(2.0, Math.max(currentIv * 2.0, currentIv + 0.05));
  return [lo, hi];
}

/** DTE sweep: [0, currentDTE]. `currentDte` is the time remaining from the
 *  scenario cursor to expiry. The initial/live cursor therefore shows the
 *  full remaining window; moving the cursor toward expiry contracts it to
 *  zero. No floor — let zero and degenerate ranges reach the math naturally. */
export function dteRange(currentDte: number): [number, number] {
  return [0, currentDte];
}

/** Strike anchor sweep — same range as price; semantics differ (re-anchors the structure). */
export function strikeRange(
  evalSpot: number,
  rangePct = DEFAULT_PRICE_RANGE_PCT,
): [number, number] {
  const halfRange = Math.max(evalSpot * rangePct, 1);
  return [evalSpot - halfRange, evalSpot + halfRange];
}

/** Moneyness % from evalSpot — [-5%, +5%] window around ATM. */
export function moneynessRange(): [number, number] {
  return [-5, 5];
}

export function sweepRange(
  field: SweepField,
  evalSpot: number,
  iv: number,
  dte: number,
  priceRangePct = DEFAULT_PRICE_RANGE_PCT,
): [number, number] {
  switch (field) {
    case "price":
      return priceRange(evalSpot, priceRangePct);
    case "strike":
      return strikeRange(evalSpot, priceRangePct);
    case "moneyness":
      return moneynessRange();
    case "iv":
      return ivRange(iv);
    case "dte":
      return dteRange(dte);
  }
}

/**
 * Clamp Z-axis bounds across all surfaces. Pnl/theta/delta use ±absMax × 1.15
 * (zero plane centered). Other fields use [min, max] + 5% padding.
 */
export function clampZBounds(
  values: Float32Array[],
  field: OutputField,
): { min: number; max: number; centerZero: boolean } {
  let min = Infinity;
  let max = -Infinity;
  for (const arr of values) {
    for (let i = 0; i < arr.length; i++) {
      const v = arr[i];
      if (Number.isFinite(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { min: -1, max: 1, centerZero: true };
  }
  if (isZeroStraddling(field)) {
    const absMax = Math.max(Math.abs(min), Math.abs(max), 1e-6) * 1.15;
    return { min: -absMax, max: absMax, centerZero: true };
  }
  if (min === max) {
    const pad = Math.max(Math.abs(min) * 0.05, 1);
    return { min: min - pad, max: max + pad, centerZero: false };
  }
  const span = max - min;
  return { min: min - span * 0.05, max: max + span * 0.05, centerZero: false };
}
